/*
 * Copyright (c) 2024. World Wide Generation Ltd
 */

import React from 'react';
import './PaymentOverdueBanner.scss';
import { Button } from 'reactstrap';
import { RootInitiativeData } from '../../types/initiative';
import G17Client from '../../services/G17Client';
import { addSiteError } from '../../slice/siteAlertsSlice';
import { useLocation } from 'react-router-dom';
import { useAppDispatch } from '@reducers/index';
import { SubscriptionService } from '../../services/SubscriptionService';
import { getDuration } from '@utils/date';

interface Props {
  organization?: Pick<RootInitiativeData, '_id' | 'calculatedSubscriptions' | 'customer'>;
}

export const PaymentOverdueBanner = ({ organization }: Props) => {
  const dispatch = useAppDispatch();
  const location = useLocation();

  if (!organization) {
    return null;
  }

  const overdueSubscription = SubscriptionService.getOverdueSubscription(organization.calculatedSubscriptions);

  if (!overdueSubscription) {
    return null;
  }

  const msRemaining = (overdueSubscription.gracePeriodEndsAt ?? 0) * 1000 - Date.now();
  const timeRemaining = getDuration(Math.max(0, msRemaining)).humanize();

  const manageSubscription = () => {
    G17Client.addPaymentDetails({
      initiativeId: organization._id,
      returnUrl: location.pathname,
    })
      .then((s) => {
        window.location.href = s.url;
      })
      .catch((e: Error) => {
        dispatch(addSiteError(e));
      });
  };

  return (
    <div className='overdue-banner-container'>
      <div className='wrapper'>
        <div className='banner-info'>
          <i className='fa fa-2x fas fa-exclamation-triangle mr-2' />
          <span>
            Your payment is overdue. You have <strong>{timeRemaining}</strong> to update your payment method before losing access.
          </span>
        </div>

        <div className='banner-content'>
          <Button size={'sm'} color='light' onClick={manageSubscription}>
            Update Payment Method
          </Button>
        </div>
      </div>
    </div>
  );
};
